import React from 'react';
import type { Showcase, ShowcaseProperty, ShowcaseResource } from '../../types';
import { MapPin } from 'lucide-react';

interface HeroSectionProps {
  showcase: Showcase;
  property: ShowcaseProperty;
  resources: ShowcaseResource[];
  brandColour: string;
  darkMode?: boolean;
}

const STATUS_COLOURS: Record<string, string> = {
  'Available': '#16a34a',
  'New Instruction': '#0891b2',
  'Price Reduced': '#dc2626',
  'Under Offer': '#d97706',
  'Sold STC': '#2563eb',
  'Sold': '#dc2626',
  'Let Agreed': '#7c3aed',
  'Coming Soon': '#4f46e5',
};

export default function HeroSection({ showcase, property, resources, brandColour, darkMode: dm }: HeroSectionProps) {
  const heroImage = resources.find((r) => r.type === 'hero_image');
  const address = [property.address_line_1, property.town_city, property.postcode_zip]
    .filter(Boolean)
    .join(', ');
  const status = showcase.property_status;

  return (
    <section className="relative w-full">
      <div className="relative min-h-[300px] overflow-hidden">
        {heroImage?.source_url ? (
          <img
            src={heroImage.source_url}
            alt={property.property_title || showcase.title}
            className="w-full h-auto block"
          />
        ) : (
          <div className="w-full h-[50vh] bg-gradient-to-br from-slate-800 to-slate-900" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        {status && (
          <div
            className="absolute top-6 right-6 px-5 py-2 rounded-lg text-sm font-bold text-white shadow-lg"
            style={{ backgroundColor: STATUS_COLOURS[status] || brandColour }}
          >
            {status}
          </div>
        )}

        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-12">
          <div className="max-w-5xl mx-auto">
            {property.price_display && (
              <span
                className="inline-block px-4 py-1.5 rounded-full text-sm font-semibold text-white mb-4"
                style={{ backgroundColor: brandColour }}
              >
                {property.price_display}
              </span>
            )}
            <h1 className="text-3xl md:text-5xl font-bold text-white mb-3">
              {property.property_title || showcase.title}
            </h1>
            {address && (
              <p className="flex items-center gap-2 text-lg text-white/80">
                <MapPin className="w-5 h-5" />
                {address}
              </p>
            )}
            {property.short_headline && (
              <p className="text-lg text-white/70 mt-3 max-w-2xl">{property.short_headline}</p>
            )}
          </div>
        </div>
      </div>

      {(property.bedrooms || property.bathrooms || property.reception_rooms || property.internal_area) && (
        <div className={`border-b transition-colors duration-300 ${dm ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'}`}>
          <div className={`max-w-5xl mx-auto px-8 py-5 flex flex-wrap gap-8 ${dm ? 'text-slate-200' : 'text-slate-700'}`}>
            {property.bedrooms && (
              <div className="text-center">
                <div className="text-2xl font-bold">{property.bedrooms}</div>
                <div className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Bedrooms</div>
              </div>
            )}
            {property.bathrooms && (
              <div className="text-center">
                <div className="text-2xl font-bold">{property.bathrooms}</div>
                <div className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Bathrooms</div>
              </div>
            )}
            {property.reception_rooms && (
              <div className="text-center">
                <div className="text-2xl font-bold">{property.reception_rooms}</div>
                <div className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Receptions</div>
              </div>
            )}
            {property.internal_area && (
              <div className="text-center">
                <div className="text-2xl font-bold">{property.internal_area}</div>
                <div className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Size</div>
              </div>
            )}
            {property.property_type && (
              <div className="text-center">
                <div className="text-2xl font-bold">{property.property_type}</div>
                <div className={`text-sm ${dm ? 'text-slate-400' : 'text-slate-500'}`}>Type</div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
